import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format currency values
export function formatCurrency(value: number, currency = "USD", showSymbol = true): string {
  if (value === undefined || value === null) {
    return "-";
  }

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: showSymbol ? 'currency' : 'decimal',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return formatter.format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${value}`;
  }
}

// Format currency with tenant context - shows amount in tenant's base currency
export function formatCurrencyWithTenant(
  value: number, 
  originalCurrency?: string, 
  tenantBaseCurrency = "USD", 
  showSymbol = true,
  showOriginal = false
): string {
  if (value === undefined || value === null) {
    return "-";
  }

  const displayCurrency = tenantBaseCurrency;
  
  // If currencies are the same, no conversion needed
  if (!originalCurrency || originalCurrency === displayCurrency) {
    return formatCurrency(value, displayCurrency, showSymbol);
  }

  // Phase 2: For now, keep Phase 1 behavior and enhance later
  const formatted = formatCurrency(value, displayCurrency, showSymbol);
  
  if (showOriginal && originalCurrency !== displayCurrency) {
    const originalFormatted = formatCurrency(value, originalCurrency, showSymbol);
    return `${formatted} (originally ${originalFormatted})`;
  }
  
  return formatted;
}

// Get currency symbol for a given currency code
export function getCurrencySymbol(currency: string): string {
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    return formatter.formatToParts(0).find(part => part.type === 'currency')?.value || currency;
  } catch (error) {
    console.error('Error getting currency symbol:', error);
    return currency;
  }
}

// Format date values
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString.toString();
  }
}

// Get status badge class
export function getStatusBadgeClass(status: string): string {
  switch (status) {
    // Claim statuses
    // Removed "Creation" status - no longer used
    case "Acknowledgment":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800";
    case "Review - Contract":
    case "Review - Quantities":
    case "Review - Calculations":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800";
    case "Information Requested":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800";
    case "Pending Internal Input":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800";
    case "Final Decision":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800";
    case "Approved":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800";
    case "Rejected":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800";
    case "Withdrawn":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800";
      
    // Project statuses
    case "setup":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800";
    case "active":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800";
    case "complete":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 text-neutral-800";
      
    // Contract statuses
    case "Draft":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800";
    // Active case is already handled above
    case "Superseded":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800";
    case "Terminated":
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800";
    
    default:
      return "px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800";
  }
}
